import React, { useCallback, useMemo, useState } from 'react';
import {
  servicesConstants,
  useCallSuccess,
  useFeatureFlagsContext,
  useI18n,
  useOpenStoreForHpApp,
  useServicesQuery,
} from '@jarvis/react-portal-addons';
import {
  IconHandEarth,
  IconPrinterInkDrop,
  IconSmartApp,
  IconTruck,
} from '@veneer/core';
import SolutionCardTemplate from './SolutionCardTemplate';
import { useCardsVisibility } from './useCardsVisibility';
import { DEEPLINKS } from '../../../utils/constants';
import {
  GetTheHpAppDownloadNowButton,
  HpAllInPlanLearnMoreLink,
  HpAllInPlanManagePlanLink,
  InstantInkLearnMoreLink,
  InstantInkManagePlanLink,
  SustainabilityLearnMoreLink,
} from '../../../utils/analytics';

const {
  SERVICE_TYPES,
  SERVICE_LEVELS,
  SERVICE_STATES,
  COMMON_QUERIES,
} = servicesConstants;

const {
  INSTANT_INK_ALL_ENABLED_QUERY,
  INSTANT_INK_ALL_OFFERING_QUERY,
} = COMMON_QUERIES;

const SolutionCards = ({ advancedView, deviceCache }) => {
  const [deviceIds, setDeviceIds] = useState([]);
  const { t } = useI18n();

  const { grantsSupport } = useFeatureFlagsContext();

  const {
    showInstantInkCard,
    showHpAllInPlanEnrolledCard,
    showHpAllInPlanOfferingCard,
    showSustainabilityCard,
    showGetHpAppCard,
    instantInkCardLinkToOverview,
  } = useCardsVisibility({ deviceCache });

  const query = useMemo(() => ({
    type: grantsSupport ? SERVICE_TYPES.GRANTS : SERVICE_TYPES.ENTITLEMENTS,
    level: SERVICE_LEVELS.ALL,
    deviceIds,
    services: [
      {
        ...INSTANT_INK_ALL_ENABLED_QUERY,
        id: 'InstantInkEnabled',
      },
      {
        ...INSTANT_INK_ALL_OFFERING_QUERY,
        id: 'InstantInkOffering',
      },
      {
        id: 'PaasEnrolled',
        serviceIds: ['ws-hp.com/paas'],
        type: SERVICE_TYPES.GRANTS,
        states: [SERVICE_STATES.ENABLED],
      },
    ],
  }), [deviceIds, grantsSupport]);

  const {
    success,
    pending,
    error,
    helpers: {
      countDeviceServices,
    },
  } = useServicesQuery(query);

  const subscriptionCardsLoading = (!success && !error) || pending || deviceCache.pending;
  const instantInkEligiblePrinterCounter = countDeviceServices({ id: 'InstantInkOffering' });
  const instantInkEnrolledPrinterCounter = countDeviceServices({ id: 'InstantInkEnabled' });
  const hpAllInEnrolledPrinterCounter = countDeviceServices({ id: 'PaasEnrolled' });

  const instantInkCardDependenciesReady = showInstantInkCard;

  const showInstantInkEnrolledCard = instantInkCardDependenciesReady && instantInkEnrolledPrinterCounter > 0;

  const showInstantInkEligibleCard = instantInkCardDependenciesReady && !showInstantInkEnrolledCard;

  const onDeviceCacheSuccess = useCallback(
    (_, devices) => {
      setDeviceIds(devices.map(({ deviceId }) => deviceId));
    },
    [],
  );

  useCallSuccess({
    call: deviceCache,
    onSuccess: onDeviceCacheSuccess,
  });

  const { openStoreForHpApp } = useOpenStoreForHpApp();

  const CardList = useMemo(() => [
    showGetHpAppCard
    && {
      analyticsData: GetTheHpAppDownloadNowButton,
      headerText: t('dashboard.getTheHpAppCard.header'),
      title: t('dashboard.getTheHpAppCard.title'),
      description: t('dashboard.getTheHpAppCard.description'),
      icon: <IconSmartApp filled />,
      onClick: openStoreForHpApp,
      footerText: t('dashboard.getTheHpAppCard.footer'),
      dataTestIds: {
        card: 'get-the-hp-app-card',
        header: 'get-the-app-card-header',
        title: 'get-the-app-card-title',
        description: 'get-the-app-card-description',
        link: 'get-the-app-card-link',
      },
    },
    showInstantInkEnrolledCard
    && {
      analyticsData: InstantInkManagePlanLink,
      headerText: instantInkEnrolledPrinterCounter === 1 ? t('dashboard.instantInkEnrolledCard.headerSingular', {
        totalPrinters: deviceCache?.data?.length || 0,
      }) : t('dashboard.instantInkEnrolledCard.headerPlural', {
        totalEnrolled: instantInkEnrolledPrinterCounter,
        totalPrinters: deviceCache?.data?.length || 0,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEnrolledCard.description'),
      icon: <IconTruck filled />,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEnrolledCard.footer'),
      showCheckmarkCircleIcon: true,
      dataTestIds: {
        card: 'instant-ink-enrolled-card',
        header: 'instant-ink-enrolled-card-header',
        title: 'instant-ink-enrolled-card-title',
        description: 'instant-ink-enrolled-card-description',
        link: 'instant-ink-enrolled-card-link',
      },
      loading: subscriptionCardsLoading,
    },
    showInstantInkEligibleCard
    && {
      analyticsData: InstantInkLearnMoreLink,
      headerText: instantInkEligiblePrinterCounter === 1 ? t('dashboard.instantInkEligibleCard.headerSingular', {
        totalEligible: instantInkEligiblePrinterCounter,
      }) : t('dashboard.instantInkEligibleCard.headerPlural', {
        totalEligible: instantInkEligiblePrinterCounter,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEligibleCard.description'),
      icon: <IconTruck filled />,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEligibleCard.footer'),
      dataTestIds: {
        card: 'instant-ink-eligible-card',
        header: 'instant-ink-eligible-card-header',
        title: 'instant-ink-eligible-card-title',
        description: 'instant-ink-eligible-card-description',
        link: 'instant-ink-eligible-card-link',
      },
      loading: subscriptionCardsLoading,
    },
    showHpAllInPlanEnrolledCard
    && {
      analyticsData: HpAllInPlanManagePlanLink,
      headerText: hpAllInEnrolledPrinterCounter === 1 ? t('dashboard.hpAllInEnrolledCard.headerSingular')
        : t('dashboard.hpAllInEnrolledCard.headerPlural', {
          totalEnrolled: hpAllInEnrolledPrinterCounter || 0,
        }),
      title: t('dashboard.hpAllInEnrolledCard.title'),
      description: t('dashboard.hpAllInEnrolledCard.description'),
      icon: <IconPrinterInkDrop filled />,
      footerHyperlink: '/hp-all-in-print-plan/overview',
      footerText: t('dashboard.hpAllInEnrolledCard.footer'),
      showCheckmarkCircleIcon: true,
      dataTestIds: {
        card: 'hp-all-in-plan-enrolled-card',
        header: 'hp-all-in-plan-enrolled-card-header',
        title: 'hp-all-in-plan-enrolled-card-title',
        description: 'hp-all-in-plan-enrolled-card-description',
        link: 'hp-all-in-plan-enrolled-card-link',
      },
      loading: subscriptionCardsLoading,
    },
    showHpAllInPlanOfferingCard
    && {
      analyticsData: HpAllInPlanLearnMoreLink,
      headerText: t('dashboard.hpAllInNotEnrolledCard.header'),
      title: t('dashboard.hpAllInNotEnrolledCard.title'),
      description: t('dashboard.hpAllInNotEnrolledCard.description'),
      icon: <IconPrinterInkDrop filled />,
      footerHyperlink:
        'https://www.hp.com/us-en/all-in-plan/printers.html?jumpid=mb_us_dca_hx_hx_cm000016_co_x_portalcard-aa',
      footerText: t('dashboard.hpAllInNotEnrolledCard.footer'),
      dataTestIds: {
        card: 'hp-all-in-plan-not-enrolled-card',
        header: 'hp-all-in-plan-not-enrolled-card-header',
        title: 'hp-all-in-plan-not-enrolled-card-title',
        description: 'hp-all-in-plan-not-enrolled-card-description',
        link: 'hp-all-in-plan-not-enrolled-card-link',
      },
    },
    showSustainabilityCard
    && {
      analyticsData: SustainabilityLearnMoreLink,
      headerText: t('dashboard.sustainabilityCard.header'),
      title: t('dashboard.sustainabilityCard.title'),
      description: t('dashboard.sustainabilityCard.description'),
      icon: <IconHandEarth filled />,
      footerHyperlink: '/forestfirst',
      footerText: t('dashboard.sustainabilityCard.footer'),
      dataTestIds: {
        card: 'sustainability-card',
        header: 'sustainability-card-header',
        title: 'sustainability-card-title',
        description: 'sustainability-card-description',
        link: 'sustainability-card-link',
      },
    },
  ].filter(Boolean), [
    deviceCache?.data?.length,
    showHpAllInPlanOfferingCard,
    hpAllInEnrolledPrinterCounter,
    instantInkCardLinkToOverview,
    subscriptionCardsLoading,
    instantInkEligiblePrinterCounter,
    instantInkEnrolledPrinterCounter,
    openStoreForHpApp,
    showGetHpAppCard,
    showHpAllInPlanEnrolledCard,
    showInstantInkEligibleCard,
    showInstantInkEnrolledCard,
    showSustainabilityCard,
    t,
  ]);

  const MAX_BASE_ACCOUNT_CARDS = 3;
  const visibleCards = useMemo(() => {
    if (!advancedView && CardList.length > MAX_BASE_ACCOUNT_CARDS) {
      const withoutGetHpApp = CardList.filter(
        card => card.dataTestIds.card !== 'get-the-hp-app-card',
      );
      return withoutGetHpApp.slice(0, MAX_BASE_ACCOUNT_CARDS);
    }
    return CardList;
  }, [advancedView, CardList]);

  if (!visibleCards.length) {
    return null;
  }

  if (advancedView && deviceCache?.data?.length === 0) {
    return null;
  }

  return visibleCards.map(cardInfo => (
    <SolutionCardTemplate
      key={cardInfo.dataTestIds.card} {...cardInfo}
      data-testid={cardInfo.dataTestIds.card}
    />
  ));
};

export default SolutionCards;
